import * as React from 'react';
import { Input } from '@/components/ui/input';
import { CountrySelect } from '@/components/ui/phone-input';
import * as RPNInput from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import { cn } from '@/lib/utils';
import { Mail, Phone, User } from 'lucide-react';
import {
  parsePhoneNumberWithError,
  parsePhoneNumberFromString,
  AsYouType,
} from 'libphonenumber-js';

interface SmartIdentifierInputProps extends React.ComponentProps<'input'> {
  value: string;
  onValueChange: (value: string) => void;
}

const NATIONAL_NUMBER_MAX_LENGTH: Partial<Record<RPNInput.Country, number>> = {
  IN: 10,
};

function getNationalMaxLength(country: RPNInput.Country): number {
  return NATIONAL_NUMBER_MAX_LENGTH[country] ?? 15;
}

function nationalDigitsFromValue(value: string, country: RPNInput.Country): string {
  const parsed = parsePhoneNumberFromString(value);
  if (parsed?.nationalNumber) return parsed.nationalNumber;

  const callingCode = RPNInput.getCountryCallingCode(country);
  if (value.startsWith(`+${callingCode}`)) {
    return value.slice(callingCode.length + 1).replace(/\D/g, '');
  }

  return value.replace(/\D/g, '');
}

export function SmartIdentifierInput({
  value,
  onValueChange,
  className,
  ...props
}: SmartIdentifierInputProps) {
  const [country, setCountry] = React.useState<RPNInput.Country>('IN');

  const isEmail = (val: string) =>
    val.includes('@') || /[a-zA-Z]/.test(val.replace(/[+()-\s]/g, ''));
  const isPhone = !isEmail(value) && /\d/.test(value);

  React.useEffect(() => {
    if (value && !isEmail(value)) {
      try {
        const phoneNumber = parsePhoneNumberWithError(value);
        if (phoneNumber?.country && phoneNumber.country !== country) {
          setCountry(phoneNumber.country);
        }
      } catch (_) {
        // ignore
      }
    }
  }, [value, country]);

  const countryOptions = React.useMemo(() => {
    return RPNInput.getCountries().map((c) => ({
      value: c,
      label: en[c],
    }));
  }, []);

  const displayValue = React.useMemo(() => {
    if (isEmail(value)) return value;
    if (!value) return '';

    try {
      const phoneNumber = parsePhoneNumberWithError(value);
      if (phoneNumber) {
        const c = phoneNumber.country || country;
        return new AsYouType(c).input(phoneNumber.nationalNumber);
      }
    } catch (_) {
      if (value.startsWith('+')) {
        const callingCode = RPNInput.getCountryCallingCode(country);
        if (value.startsWith(`+${callingCode}`)) {
          const national = value.slice(callingCode.length + 1);
          return new AsYouType(country).input(national);
        }
      }
    }
    return value;
  }, [value, country]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;

    if (isEmail(inputVal)) {
      onValueChange(inputVal);
      return;
    }

    const digits = inputVal.replace(/[^0-9]/g, '');
    if (!digits) {
      onValueChange('');
      return;
    }

    const trimmed = inputVal.trim();
    const callingCode = RPNInput.getCountryCallingCode(country);
    const maxNationalLength = getNationalMaxLength(country);

    if (trimmed.startsWith('+')) {
      const parsed = parsePhoneNumberFromString(trimmed);
      if (parsed?.isValid()) {
        onValueChange(parsed.number);
        return;
      }
    }

    const likelyFullInternationalPaste =
      digits.startsWith(callingCode) && digits.length > maxNationalLength;

    if (likelyFullInternationalPaste) {
      const parsed = parsePhoneNumberFromString(`+${digits}`);
      if (parsed?.isValid()) {
        onValueChange(parsed.number);
        return;
      }
    }

    onValueChange(`+${callingCode}${digits}`);
  };

  const handleCountryChange = (newCountry: RPNInput.Country) => {
    setCountry(newCountry);
    const currentNational = nationalDigitsFromValue(value, country);
    const newCallingCode = RPNInput.getCountryCallingCode(newCountry);
    if (currentNational) {
      onValueChange(`+${newCallingCode}${currentNational}`);
    }
  };

  const showCountrySelect = isPhone;

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none">
        {showCountrySelect ? (
          <Phone className="h-4 w-4" />
        ) : isEmail(value) && value ? (
          <Mail className="h-4 w-4" />
        ) : (
          <User className="h-4 w-4" />
        )}
      </div>

      <Input
        value={displayValue}
        onChange={handleInputChange}
        className={cn(showCountrySelect ? 'pl-12 pr-24' : 'pl-10', className)}
        placeholder={showCountrySelect ? 'Phone Number' : 'Email or Phone'}
        type={isEmail(value) ? 'email' : 'tel'}
        {...props}
      />

      {showCountrySelect && (
        <div className="absolute right-0 top-0 h-full flex items-center bg-transparent">
          <div className="h-4/6 w-px bg-border my-auto" />
          <CountrySelect
            value={country}
            onChange={handleCountryChange}
            options={countryOptions}
            disabled={false}
            className="h-full rounded-l-none rounded-r-md border-0 bg-transparent hover:bg-transparent px-3"
          />
        </div>
      )}
    </div>
  );
}
